import { useUpdateEventMutation } from '@pages/event/api/useUpdateEventMutation';
import { AddClientsToEventModal, useSminexClubEventParticipantsFromWs } from '@entities/clients';
import { ActivitySwitcher } from '@pages/event/ui/activity-switcher';
import { ChevronLeftIconRounded, PlusIcon } from '@shared/assets/icons';
import { EVENT_TABS_IDS } from '../../model/consts';
import { ListParticipantsTab } from './ListParticipantsTab';
import { useNavigate, useParams } from 'react-router-dom';
import { IEvent } from '@pages/event/api/useFetchEvent';
import styles from './event-sminex-club.module.scss';
import { IconButton } from '@shared/ui/icon-button';
import { useToast } from '@shared/hooks/useToast';
import { OrganizersTab } from './OrganizersTab';
import { useCallback, useState } from 'react';
import { EventSminexClubSwitcher } from './EventSminexClubSwitcher';
import { useEventPageWsConnection } from '@shared/api/event-page-ws';
import InputSearch from '@shared/ui/input-search';
import { Button } from '@shared/ui/kit';
import { colors } from '@shared/tokens/colors.stylex';

interface EventSminexClubProps {
  event?: IEvent;
}

export const EventSminexClub = ({ event }: EventSminexClubProps) => {
  const eventName = event?.name || '';
  const truncatedTitle = eventName.length > 55 ? eventName.slice(0, 55) + '...' : eventName;
  const { isPendingTickets, isPendingInvites } = event?.metadata ?? {};
  const [activeTabId, setActiveTabId] = useState(EVENT_TABS_IDS.LIST_PARTICIPANTS);
  const [searchValue, setSearchValue] = useState('');
  const [isOpenAddClientsModal, setIsOpenAddClientsModal] = useState(false);
  const [, updateEvent] = useUpdateEventMutation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { id } = useParams();
  const eventId = Number(id) || 0;

  useEventPageWsConnection(eventId);

  const { clients, allClients, fetching, handleSearchChange, handleOrderChange, handleFilterChange, refetch } =
    useSminexClubEventParticipantsFromWs(eventId);

  const getStatusText = () => {
    if (isPendingTickets && isPendingInvites) {
      return 'Идёт отправка билетов и приглашений';
    }

    if (isPendingTickets) {
      return 'Идёт отправка билетов';
    }

    if (isPendingInvites) {
      return 'Идёт отправка приглашений';
    }
  };

  const handleToggleActive = useCallback(async () => {
    if (!event) return;

    const newActiveState = !event.active;

    try {
      const { error } = await updateEvent({ id: event.id, active: newActiveState });

      if (error) {
        const message = error.message.includes("don't have access")
          ? 'У вас нет прав на изменение мероприятия'
          : 'Ошибка при обновлении события';

        showToast(message, 'error');
        return;
      }

      showToast('Настройки успешно сохранены', 'success');
    } catch {
      showToast('Ошибка при обновлении события', 'error');
    }
  }, [event, updateEvent, showToast]);

  return (
    <div className={styles.container}>
      <div className={styles.backButton}>
        <IconButton onClick={() => navigate(-1)} icon={<ChevronLeftIconRounded />} variant="primary" size="small">
          Назад
        </IconButton>
      </div>

      <div className={styles.header}>
        <span className={styles.name}>{truncatedTitle}</span>

        <ActivitySwitcher
          isActive={event?.active}
          dateStart={event?.dateEnd}
          statusText={getStatusText()}
          onToggle={handleToggleActive}
        />
      </div>

      <div className={styles.toolbar}>
        <EventSminexClubSwitcher activeTabId={activeTabId} onChange={setActiveTabId} />

        {activeTabId === EVENT_TABS_IDS.LIST_PARTICIPANTS && (
          <div className={styles.actions}>
            <div className={styles.search}>
              <InputSearch
                searchValue={searchValue}
                changeValue={setSearchValue}
                clearHandler={() => setSearchValue('')}
              />
            </div>

            <Button
              size="small"
              settings={{ width: '117px', fontSize: '14px', lineHeight: 'var(--line-height-caption-1)' }}
              onClick={() => setIsOpenAddClientsModal(true)}
            >
              <PlusIcon color={colors.inverseIcon} /> Добавить
            </Button>
          </div>
        )}
      </div>

      {activeTabId === EVENT_TABS_IDS.LIST_PARTICIPANTS ? (
        <ListParticipantsTab
          clients={clients}
          allClients={allClients}
          fetching={fetching}
          eventId={eventId}
          searchValue={searchValue}
          handleSearchChange={handleSearchChange}
          handleOrderChange={handleOrderChange}
          handleFilterChange={handleFilterChange}
          refetch={refetch}
          dateStart={event?.dateStart}
        />
      ) : (
        <OrganizersTab eventId={Number(id)} />
      )}

      <AddClientsToEventModal
        isOpen={isOpenAddClientsModal}
        onClose={() => setIsOpenAddClientsModal(false)}
        availableClients={allClients}
        refetchClients={refetch}
      />
    </div>
  );
};
